import { z } from 'zod';
import { InvestmentDTO, CapexDTO, OpexDTO, FinancingDTO } from './dto';
import { InvestmentType, InvestmentStatus, PsiFeeType } from '../../index';

export type CoInvestorInvitationStatus = 'PENDING' | 'ACCEPTED';
export type CoInvestorContractStatus = 'NOT_GENERATED' | 'PENDING_SIGNATURE' | 'SIGNED';

export interface CoInvestorDTO {
  id: string;
  email: string;
  investorId?: string;
  investorName?: string;
  investorPhotoUrl?: string;
  ownershipPercentage: number;
  invitationStatus: CoInvestorInvitationStatus;
  contractStatus: CoInvestorContractStatus;
  isPaid: boolean;
  feeType?: PsiFeeType;
  feeAmount?: number;
  feePercentage?: number;
}

export interface CoInvestorInput {
  email: string;
  ownershipPercentage: number;
  isPaid?: boolean;
  feeType?: PsiFeeType;
  feeAmount?: number;
  feePercentage?: number;
}

export interface CreateCoInvestmentRequestBody {
  address: string;
  city: string;
  province: string;
  postalCode: string;
  type: InvestmentType;
  minContributionAmount: number;
  maxContributionAmount: number;
  fundraisingEndDate: string;
  propertyRegistryVolume?: string;
  propertyRegistryBook?: string;
  propertyRegistryFolio?: string;
  propertyRegistryEntry?: string;
  propertyRegistryPropertyNumber?: string;
  propertyCadastralReference?: string;
  status?: InvestmentStatus;
  capex: Omit<CapexDTO, 'total'>;
  rentedSince?: string;
  monthlyRent?: number;
  opex?: OpexDTO;
  financing?: FinancingDTO;
  coInvestors: CoInvestorInput[];
}

export interface CreateCoInvestmentResponse {
  data: InvestmentDTO;
}

export interface CoInvestorListResponse {
  data: CoInvestorDTO[];
}

export interface AcceptCoInvestmentInvitationRequestBody {
  token: string;
}

export interface UpdateCoInvestorPaidStatusRequestBody {
  isPaid: boolean;
}

export interface MyCoInvestorContractResponse {
  data: {
    status: CoInvestorContractStatus;
    url?: string;
  };
}

export const UpdateCoInvestorsRequestSchema = z.object({
  coInvestors: z
    .array(
      z.object({
        email: z.string().trim().toLowerCase().email('Introduce un email válido'),
        ownershipPercentage: z
          .number({ invalid_type_error: 'Introduce un importe válido' })
          .finite()
          .gt(0, 'Cada coinversor debe tener una participación mayor que cero')
          .lte(1, 'La participación no puede superar el 100%'),
      })
    )
    .min(2, 'Se necesitan al menos dos coinversores')
    .refine(
      (rows) => new Set(rows.map((row) => row.email)).size === rows.length,
      'No puede haber emails duplicados'
    )
    .refine(
      (rows) => Math.abs(rows.reduce((sum, row) => sum + row.ownershipPercentage, 0) - 1) <= 0.0001,
      'La participación total debe sumar el 100%'
    ),
});

export type UpdateCoInvestorsRequestBody = z.infer<typeof UpdateCoInvestorsRequestSchema>;
